// dependencies
import React from "react";
import moment from "moment";
import { Link } from "yarrr";
import PropTypes from "prop-types";
import { List, Map } from "immutable";
import { connect } from "react-redux";
import Fuse from "fuse.js";
import { BranchSelect } from "../../Components/BranchSelector";
import { isDeploymentDateOutdated } from "../../Utils/dateUtilities";

// config
import config from "./config";

// components
import Button from "Components/Button";
import Spinner from "Components/Spinner";
import TextInput from "Components/Inputs/Text";

// actions
import { init, teardownComponent, teardownDeployment, deployBranch, updateDeployment } from "./actions";

// selectors
import { getIsRequesting } from "Selectors/app";
import { getDeployments, getBranches } from "./selectors";

// styles
import css from "./Deployments.styl";

class Deployments extends React.PureComponent {
	static propTypes = {
		isDeploying: PropTypes.bool.isRequired,
		init: PropTypes.func.isRequired,
		teardown: PropTypes.func.isRequired,
		teardownDeployment: PropTypes.func.isRequired,
		deployBranch: PropTypes.func.isRequired,
		updateDeployment: PropTypes.func.isRequired,
		branches: PropTypes.array.isRequired,
		deployments: PropTypes.instanceOf(List).isRequired,
	};

	state = {
		tearingDown: [],
		updatingDeployments: [],
		selectedBranch: null,
		searchTerm: "",
		filteredDeployments: [],
		fuse: new Fuse([], { keys: ["branch", "log.userDisplayName"] }),
	};

	componentDidMount = () => {
		this.props.init();
	};

	componentDidUpdate = (prevProps) => {
		if (!prevProps.deployments.equals(this.props.deployments)) {
			const deploymentsAsArray = this.props.deployments.toJS();
			this.state.fuse.setCollection(deploymentsAsArray);
			const currentSearch = this.state.searchTerm;
			this.state.filteredDeployments = currentSearch
				? this.state.fuse.search(this.state.searchTerm).map((deployment) => Map(deployment.item))
				: this.props.deployments;
		}
	};

	componentWillUnmount = () => {
		this.props.teardown();
	};

	teardownDeployment = (domain, deploymentLogID) => {
		if (deploymentLogID) {
			const teardownList = this.state.tearingDown.slice();
			teardownList.push(deploymentLogID);

			this.setState({ tearingDown: teardownList });
		}

		this.props.teardownDeployment(domain);
	};

	handleBranchSelect = (value) => {
		this.setState({ selectedBranch: value });
	};

	handleDeployBranch = (deploymentType) => {
		const { selectedBranch } = this.state;
		this.props.deployBranch(selectedBranch.commitHash, selectedBranch.branchName, deploymentType);
		this.setState({ selectedBranch: null });
	};

	handleUpdateDeployment = (domain, nextCommit, deploymentType) => {
		this.props.updateDeployment(domain, nextCommit, deploymentType);

		const updateList = this.state.updatingDeployments.slice();
		updateList.push(domain);
		this.setState({ updatingDeployments: updateList });
	};

	handleSearchChange = (searchTerm) => {
		if (searchTerm) {
			const resultDeployments = this.state.fuse.search(searchTerm).map((deployment) => Map(deployment.item));
			const filteredDeployments = List(resultDeployments);
			this.setState({ searchTerm, filteredDeployments });
		} else {
			this.setState({ searchTerm, filteredDeployments: this.props.deployments });
		}
	};

	render = () => {
		const { selectedBranch, tearingDown, searchTerm, filteredDeployments } = this.state;
		const { branches, isDeploying } = this.props;

		return (
			<div className={css.main}>
				<div className={css.container}>
					<div className={css.header}>
						{branches.length > 0 ? (
							<div className={css.branchSelectorContainer}>
								<div className={css.selectionWrapper}>
									<BranchSelect value={selectedBranch} onChange={this.handleBranchSelect} />
									<Button
										isRequesting={isDeploying}
										onClick={() => this.handleDeployBranch("manualTesting")}
									>
										Deploy
									</Button>
								</div>
							</div>
						) : null}
						<div className={css.searchboxWrapper}>
							<div className={css.label}>Search Deployments</div>
							<TextInput value={searchTerm} onChange={this.handleSearchChange} />
						</div>
					</div>
					<div className={css.deployments}>
						<div className={css.table}>
							<div className={css.tableHead}>
								<div className={`${css.headCell}`} />
								<div className={`${css.headCell}`}>Domain</div>
								<div className={`${css.headCell}`}>Branch</div>
								<div className={`${css.headCell}`}>Hash</div>
								<div className={`${css.headCell}`}>Deployer</div>
								<div className={`${css.headCell}`}>Deployed Date</div>
								<div className={`${css.headCell}`} />
								<div className={`${css.headCell}`} />
								<div className={`${css.headCell}`} />
							</div>
							<div className={css.tableBody}>
								{filteredDeployments.map((deployment) => {
									const domain = deployment.get("domain");
									const branch = deployment.get("branch");

									const githubBranch = branches.find((item) => item.label === branch);
									const branchNewestCommit = githubBranch ? githubBranch.commitHash : null;
									const newerCommitExists = branchNewestCommit !== deployment.get("hash");
									const nextUpdateIsReady = deployment.get("nextUpdateIsReady", false);

									const logID = deployment.getIn(["log", "deployedDate"], null);
									const lastEvent = deployment.getIn(["log", "lastEvent"], null);
									const isTearingDown = tearingDown.indexOf(logID) >= 0;

									let status = "waiting";
									if (deployment.get("healthy") === true) {
										status = "healthy";
									} else if (deployment.get("healthy") === false) {
										status = "sick";
									}

									const renderUpdateButton =
										!isTearingDown &&
										githubBranch &&
										newerCommitExists &&
										nextUpdateIsReady &&
										lastEvent !== "is-updating";

									return (
										<div
											key={deployment.get("id")}
											className={`${css.deploymentRow} ${
												lastEvent === "teardown" ? css.isTearingDown : ""
											}`}
										>
											<div className={`${css.bodyCell} ${css.statusCell}`}>
												<div className={`${css.statusIndicator} ${css[status]}`} />
											</div>

											<div className={`${css.bodyCell}`}>{deployment.get("label")}</div>

											<div className={`${css.bodyCell} ${css.noWrap}`}>
												<a
													className={css.branchLink}
													target="_blank"
													rel="noopener noreferrer"
													href={`https://github.com/PerformYard/PerformYard/commits/${branch}`}
												>
													{branch}
												</a>
											</div>

											<div className={`${css.bodyCell} ${css.hash}`}>
												{deployment.get("hash", "").slice(0, 7)}
											</div>

											<div className={`${css.bodyCell} ${css.deployer}`}>
												{deployment.getIn(["log", "userDisplayName"], "")}
											</div>

											<div
												className={`${css.bodyCell} ${css.deployedDate} ${
													isDeploymentDateOutdated(deployment) ? css.oldDate : ""
												}`}
											>
												{moment(deployment.getIn(["log", "deployedDate"], "")).calendar()}
											</div>

											<div className={`${css.bodyCell}`}>
												{isTearingDown ? null : (
													<Link
														className={css.domain}
														isExternal={true}
														href={`https://${domain}.staging.talent.performyard.com`}
													>
														<Button size="medium" className={css.bodyButton}>
															Go
														</Button>
													</Link>
												)}
											</div>

											{renderUpdateButton ? (
												<div className={`${css.bodyCell}`}>
													<Button
														size="medium"
														onClick={() =>
															this.handleUpdateDeployment(
																domain,
																branchNewestCommit,
																deployment.get("deploymentType")
															)
														}
														className={css.bodyButton}
														color="green"
													>
														Update
													</Button>
												</div>
											) : null}

											{lastEvent === "is-updating" ? (
												<div className={`${css.bodyCell} ${css.spinnerCell}`}>
													<Spinner color="green" />
												</div>
											) : null}

											<div className={`${css.bodyCell}`}>
												{isTearingDown ? (
													<Spinner />
												) : (
													<Button
														size="medium"
														onClick={() => this.teardownDeployment(domain, logID)}
														className={css.bodyButton}
														color="red"
													>
														Teardown
													</Button>
												)}
											</div>
										</div>
									);
								})}
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	};
}

const mapStateToProps = (state) => ({
	branches: getBranches(state),
	deployments: getDeployments(state),
	isFetching: getIsRequesting(state, "DEPLOYMENTS:GET"),
	isDeploying: getIsRequesting(state, "DEPLOYMENTS:CREATE"),
});

const mapDispatchToProps = (dispatch) => ({
	init: () => dispatch(init()),
	teardown: () => dispatch(teardownComponent()),
	teardownDeployment: (domain) => dispatch(teardownDeployment(domain)),
	deployBranch: (commitHash, deploymentType) => dispatch(deployBranch(commitHash, deploymentType)),
	updateDeployment: (domain, nextCommit, deploymentType) =>
		dispatch(updateDeployment(domain, nextCommit, deploymentType)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Deployments);
